import {useSelector, shallowEqual} from 'react-redux';

import {PRESCRIPTION_STATUS} from 'constants/success';
import {getOrderDetails} from 'desktop/pages/success/selectorsV2';
import {
    getIsAllCLPrescriptionsHasEqualStatus,
    getIsAllContacts,
    getIsFrameSkipPrescription,
    getIsNotAllContactsHasSamePrescription,
    getPrescriptionStatus,
} from 'desktop/pages/success/selectorsV2/prescription';
import {getOrderItems} from 'desktop/pages/success/store/selectors/orderStatus/orderStatus';
import {OrderedItemType} from 'desktop/pages/success/types/orderItemsTypes';
import {ApplicationStoreType} from 'desktop/store';
import {getIsAllItemsOfOrderUpdated} from 'libs/success/helpers/helpers';
import {getCommonPrescriptionStatusAfterUpdateOrder} from 'libs/success/prescription/prescription';

import {ItemsType, UPLOAD} from 'types/successPage';

const shouldShowPrescriptionStatusAfterUpdate = (prescriptionStatus: string, items: ItemsType) => {
    const isAllItemsSuccess = getIsAllItemsOfOrderUpdated(items);

    return (
        prescriptionStatus !== PRESCRIPTION_STATUS.MISSING_ITEM_PRESCRIPTION ||
        isAllItemsSuccess ||
        Boolean(items?.find(item => item.prescriptionStatus === PRESCRIPTION_STATUS.EYE_EXAM))
    );
};

const getShouldShowMixedStatus = (
    isAllContacts: boolean,
    isContactsSuccess: boolean,
    isNotSameRxMethod: boolean,
) => !isAllContacts && ((isContactsSuccess && !isNotSameRxMethod) || isNotSameRxMethod);

/**
 * Gets the current prescription status
 */
export const usePrescriptionStatus = () => {
    const {
        isAllContacts,
        isFrameSkipPrescription,
        isNotSameRxMethod,
        items,
        orderItems,
        prescriptionStatus,
    } = useSelector(
        (store: ApplicationStoreType) => ({
            isAllContacts: getIsAllContacts(store),
            isFrameSkipPrescription: getIsFrameSkipPrescription(store),
            isNotSameRxMethod: getIsNotAllContactsHasSamePrescription(store),
            items: getOrderItems(store),
            orderItems: getOrderDetails(store),
            prescriptionStatus: getPrescriptionStatus(store),
        }),
        shallowEqual,
    );

    const prescriptionStatusAfterUpdate = getCommonPrescriptionStatusAfterUpdateOrder(
        items,
        orderItems.items.length,
    );
    const isAllItemsSuccess = getIsAllItemsOfOrderUpdated(items);

    const isAllContactsUpload = getIsAllCLPrescriptionsHasEqualStatus(
        orderItems.items as unknown as OrderedItemType[],
        UPLOAD,
    );

    const isContactsSuccess = isAllItemsSuccess || isAllContactsUpload;

    // If eyeglasses are in the order and contacts status is not success, show missing details text
    if (getShouldShowMixedStatus(isAllContacts, isContactsSuccess, isNotSameRxMethod)) {
        return isFrameSkipPrescription || !isContactsSuccess
            ? PRESCRIPTION_STATUS.MISSING_ITEM_PRESCRIPTION
            : PRESCRIPTION_STATUS.MIXED_ITEMS_SUCCESS;
    }

    // Else, if status is not missing item prescription or there is an eye exam - show the prescriptionStatusAfterUpdate. If it is false or empty, show the current prescriptionStatus
    return (
        (items?.length &&
            shouldShowPrescriptionStatusAfterUpdate(prescriptionStatus, items) &&
            prescriptionStatusAfterUpdate) ||
        prescriptionStatus
    );
};
